import 'moment-timezone';
import { catchError, map, of } from 'rxjs';

import * as uuid from 'uuid';

import { Injectable } from '@angular/core';
import {
	deleteAllEntities,
	deleteEntities,
	getActiveEntity,
	getEntity,
	resetActiveId,
	resetActiveIds,
	selectActiveEntity,
	selectEntity,
	setActiveId,
	updateEntities,
	upsertEntitiesById,
} from '@ngneat/elf-entities';
import { clearRequestsCache, isRequestCached, selectRequestStatus, updateRequestCache, updateRequestStatus } from '@ngneat/elf-requests';
import { ConfigService } from '@shared/services/core/config.service';

import { BaseRepository } from '../base.repository';
import { ReservationLaunch } from './active-reservation.interface';
import { store } from './active-reservation.store';

@Injectable({ providedIn: 'root' })
export class ActiveReservationRepository extends BaseRepository {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - selects
	 **-------------------------------------------------------------------------------------
	 */
	selectReservationsStatus$ = id => store.pipe(selectRequestStatus(`activeReservations`));
	selectReservationsStatusById$ = id => store.pipe(selectRequestStatus(`activeReservation-${id}`));
	selectActiveLaunch$ = store.pipe(selectActiveEntity());
	selectSafetyQuestions$ = id => store.pipe(selectEntity(id, { pluck: e => e.registration.safetyQuestions }));
	selectEquipmentQuestions$ = id =>
		store.pipe(
			selectEntity(id, {
				pluck: e => [...e.registration.equipmentQuestions],
			}),
			map(
				equipmentQuestions =>
					equipmentQuestions?.map((question, index) => ({
						id: index,
						...question,
						checked: question.checked ?? false,
					}))
			),
			catchError(error => {
				console.error('Error in selectEquipmentQuestions$', error);
				return of([]);
			})
		);
	selectEquipmentCheckQuestions$ = id =>
		store.pipe(
			selectEntity(id, {
				pluck: e => [...e.registration.companyEquipmentQuestions],
			}),
			map(
				companyEquipmentQuestions =>
					companyEquipmentQuestions?.map((question, index) => ({
						id: index,
						...question,
						selected: question.selected ? question.selected : question.quantity,
						checked: question.checked ? question.checked : false,
					}))
			),
			catchError(error => {
				console.error('Error in selectEquipmentCheckQuestions$', error);
				return of([]);
			})
		);

	selectMainInvoice = id => store.pipe(selectEntity(id, { pluck: e => e.registration.invoice }));
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - queries
	 **-------------------------------------------------------------------------------------
	 */
	get = (id, key) => store.query(getEntity(id))?.[key];
	queryActiveLaunch = id => store.query(getEntity(id));
	queryProduct = id => store.query(getEntity(id)).registration.product;
	queryRequiredEquipmentPhotos = id => store.query(getEntity(id))?.requiredEquipmentPhotos || [];
	queryOptionalEquipmentPhotos = id => store.query(getEntity(id))?.optionalEquipmentPhotos || [];
	queryUploadedEquipmentPhotos = id => store.query(getEntity(id))?.uploadedEquipmentPhotos || [];
	selectEquipmentCheckQuestions = id => store.query(getEntity(id))?.registration?.companyEquipmentQuestions || [];
	selectEquipmentQuestions = id => store.query(getEntity(id))?.registration?.equipmentQuestions || [];
	queryIsCached = id => store.query(isRequestCached(`activeReservation-${id}`));
	queryPayment = id => store.query(getEntity(id)).registration.payments;
	queryMainInvoice = id => store.query(getEntity(id)).registration.invoice;
	queryManifest = id => store.query(getEntity(id)).passengers;
	queryLicensePath = id => store.query(getEntity(id))?.identity?.licensePath;
	queryCompany = id => store.query(getEntity(id))?.registration?.company;
	queryCompanySettings = id => store.query(getEntity(id))?.registration?.companySettings;
	queryManifestIsValid = id =>
		store
			.query(getEntity(id))
			?.passengers?.filter(e => !e.deleted)
			.every(e => e.documentsSigned);
	queryCustomer = id => store.query(getEntity(id)).registration.customer;
	queryInsurance = id => store.query(getEntity(id)).registration.reservationInsurance;
	queryReservationData = id => store.query(getEntity(id)).registration.reservationData;

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(private readonly env: ConfigService) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - mutations
	 **-------------------------------------------------------------------------------------
	 */
	clearStore = () => {
		store.update(resetActiveIds());
		store.update(deleteAllEntities());
	};
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */

	setStatus = (id, key) => {
		store.update(updateRequestStatus(`activeReservation-${id}`, key));
	};
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */

	set = (id, key, data) => {
		store.update(updateEntities(id, { [key]: data }));
		return this;
	};
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setActiveReservation
	 **-------------------------------------------------------------------------------------
	 */
	public setActive = id => store.update(setActiveId(id));

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - update
	 **-------------------------------------------------------------------------------------
	 */
	public update(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				...data,
			}))
		);
	}

	public updateCompanyEquipmentQuestions(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				registration: {
					...entity.registration,
					companyEquipmentQuestions: data,
				},
			}))
		);
	}

	public updateEquipmentQuestions(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				registration: {
					...entity.registration,
					equipmentQuestions: data,
				},
			}))
		);
	}

	public updateLegacyEquipmentPhotos(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				optionalEquipmentPhotos: data.filter(e => !e.required),
				requiredEquipmentPhotos: data.filter(e => e.required),
			}))
		);
	}

	public addUploadedEquipmentPhoto(id: ReservationLaunch['reservation_id'], newPhoto) {
		store.update(
			updateEntities(id, entity => {
				const existingPhoto = entity.uploadedEquipmentPhotos?.find(e => e.productTypePhotoId === newPhoto.productTypePhotoId);
				const uploadedEquipmentPhotos = entity.uploadedEquipmentPhotos || [];

				if (existingPhoto) {
					return {
						...entity,
						uploadedEquipmentPhotos: uploadedEquipmentPhotos.map(e => {
							if (e.productTypePhotoId === newPhoto.productTypePhotoId) return newPhoto;
							return e;
						}),
					};
				}

				return {
					...entity,
					uploadedEquipmentPhotos: [...uploadedEquipmentPhotos, newPhoto],
				};
			})
		);
	}

	public removeUploadedEquipmentPhoto(id: ReservationLaunch['reservation_id'], productTypePhotoId) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				uploadedEquipmentPhotos: entity.uploadedEquipmentPhotos?.filter(e => e.productTypePhotoId !== productTypePhotoId) || [],
			}))
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateProduct
	 **-------------------------------------------------------------------------------------
	 */
	public updateProduct(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				product: {
					...entity.identity,
					...data,
				},
				registration: {
					...entity.registration,
					product: {
						...entity.registration.product,
						...data,
					},
					reservationData: {
						...entity.registration.reservationData,
						name: data.name,
						inventory_type_id: data.pricing_inventory_type_id,
						product_id: data.product_id,
						product_type: data.productProductTypes[0].product_types.product_type,
					},
					//TODO: not every field is being updated, just the ones that are needed to satisfy insurance binding
				},
			}))
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateEquipment
	 **-------------------------------------------------------------------------------------
	 */
	public updateEquipment(id: ReservationLaunch['reservation_id'], dataItems: any[]) {
		store.update(
			updateEntities(id, entity => {
				const updatedEntity = { ...entity };
				updatedEntity.registration = { ...entity.registration };
				dataItems.forEach(data => {
					updatedEntity.registration.companyEquipmentQuestions = updatedEntity.registration.companyEquipmentQuestions.map(e => {
						if (e.company_product_type_equipment_check_configuration_id === data.company_product_type_equipment_check_configuration_id)
							return data;
						return e;
					});
					updatedEntity.registration.equipmentQuestions = updatedEntity.registration.equipmentQuestions.map(e => {
						if (e.question === data.question) return data;
						return e;
					});
				});
				return updatedEntity;
			})
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateReservationInsurance
	 **-------------------------------------------------------------------------------------
	 */
	public updateReservationInsurance(id: ReservationLaunch['reservation_id'], guid: string) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				registration: {
					...entity.registration,
					reservationInsurance: {
						insurance_reservation_guid: guid,
					},
				},
			}))
		);
	}

	public updateReservationData(id: ReservationLaunch['reservation_id'], reservationData) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				registration: {
					...entity.registration,
					reservationData: {
						...entity.registration.reservationData,
						...reservationData,
					},
				},
			}))
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateReview
	 **-------------------------------------------------------------------------------------
	 */
	public updateReview(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => {
				return {
					...entity,
					review: {
						...entity.review,
						...data,
					},
				};
			})
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateFinalize
	 **-------------------------------------------------------------------------------------
	 */
	public updateFinalize(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => ({
				...entity,
				finalize: {
					...entity.finalize,
					...data,
				},
			}))
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - delete
	 **-------------------------------------------------------------------------------------
	 */
	public delete(id) {
		store.update(deleteEntities(id));
		store.update(resetActiveId());
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateIdentity
	 **-------------------------------------------------------------------------------------
	 */
	public updateIdentity(id: ReservationLaunch['reservation_id'], data) {
		store.update(
			updateEntities(id, entity => {
				return {
					...entity,
					identity: {
						...entity?.identity,
						...data,
					},
				};
			})
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clearCache
	 **-------------------------------------------------------------------------------------
	 */
	public clearCache(): void {
		store.update(clearRequestsCache());
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setPassengers
	 **-------------------------------------------------------------------------------------
	 */
	public setPassengers(id: ReservationLaunch['reservation_id'], passengers) {
		store.update(
			updateEntities(id, entity => {
				return {
					...entity,
					passengers: passengers.map((i, idx) => ({
						...(entity.passengers?.find(e => e.reservation_additional_operator_id === i.reservation_additional_operator_id) || {}),
						uuid: uuid.v4(),
						reservation_additional_operator_id: i.reservation_additional_operator_id,
						name: i.operator_name,
						email: i.email,
						dob: i.operator_dob,
						type: this.getType(i, idx),
						isDriving: i.is_operator,
						isMinor: i.is_minor,
						hasError: false,
						signature: i.signature,
						deleted: i.deleted,
						marina_signature: null,

						documents: {
							waiver: i.signed_waiver,
							rentalAgreement: i.signed_rental_agreement,
							navRules: i.signed_navigation_rules,
							acknowledgement: i.signed_acknowledgement,
						},
						documentsSigned: i.all_documents_signed,
					})),
				};
			})
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - addLaunch
	 **-------------------------------------------------------------------------------------
	 */
	public async addLaunch(reservation, state) {
		const passengers = reservation.passengers;
		store.update(
			updateRequestCache(`activeReservation-${reservation.reservationData.reservation_id}`, {
				value: 'full',
			}),
			upsertEntitiesById(reservation.reservationData.reservation_id, {
				updater: entity => {
					return {
						...entity,
						reservation_id: reservation.reservationData.reservation_id,
						registration: { ...reservation, ...entity.registration },
						state,
						customer_id: reservation.reservationData.customer_id,
						passengers: [
							...passengers.map((i, idx) => ({
								...(entity.passengers?.find(e => e.reservation_additional_operator_id === i.reservation_additional_operator_id) ||
									{}),
								uuid: uuid.v4(),
								reservation_additional_operator_id: i.reservation_additional_operator_id,
								name: i.operator_name,
								email: i.email,
								dob: i.operator_dob,
								type: this.getType(i, idx),
								isDriving: i.is_operator,
								isMinor: i.is_minor,
								hasError: false,
								signature: i.signature,
								deleted: i.deleted,
								marina_signature: null,
								documents: {
									waiver: i.signed_waiver,
									rentalAgreement: i.signed_rental_agreement,
									navRules: i.signed_navigation_rules,
									acknowledgement: i.signed_acknowledgement,
								},
								documentsSigned: i.all_documents_signed,
							})),
						],
					};
				},
				creator: id => ({
					reservation_id: id,
					registration: { ...reservation },
					customer_id: reservation.reservationData.customer_id,
					state,
					assignedEmployeeId: reservation.reservationData.assigned_employee_id,
					passengers: passengers.map((i, idx) => ({
						uuid: uuid.v4(),
						reservation_additional_operator_id: i.reservation_additional_operator_id,
						name: i.operator_name,
						email: i.email,
						dob: i.operator_dob,
						type: this.getType(i, idx),
						isDriving: i.is_operator,
						isMinor: i.is_minor,
						hasError: false,
						signature: i.signature,
						marina_signature: null,

						deleted: i.deleted,
						documents: {
							waiver: i.signed_waiver,
							rentalAgreement: i.signed_rental_agreement,
							navRules: i.signed_navigation_rules,
							acknowledgement: i.signed_acknowledgement,
						},
						documentsSigned: i.all_documents_signed,
					})),
				}),
			}),
			setActiveId(reservation.reservationData.reservation_id),
			updateRequestStatus(`activeReservation-${reservation.reservationData.reservation_id}`, 'success')
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getType
	 **-------------------------------------------------------------------------------------
	 */
	public getType(value, i) {
		if (value.is_operator && i == 0) return 'operator';
		if (value.is_operator && i > 0) return 'additional_operator';
		if (value.is_minor) return 'minor';
		return 'passenger';
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - resetActiveReservation
	 **-------------------------------------------------------------------------------------
	 */
	public async resetActiveReservation(id) {
		store.update(resetActiveId());
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updatePassengerManifest
	 **-------------------------------------------------------------------------------------
	 */
	public updatePassengerManifest(passenger, type: string = 'update') {
		const { passengers, reservation_id } = store.query(getActiveEntity());
		const updater = passengers => {
			if (type === 'update') {
				return passengers.map(e => {
					if (e.uuid === passenger.uuid) {
						return { ...e, ...passenger };
					}
					return e;
				});
			}
			if (type === 'insert') return [...passengers, { ...this.selectTemplatePassenger(), ...passenger }];
			if (type === 'remove')
				return passengers.map(e => {
					if (e.uuid === passenger.uuid) {
						return { ...e, ...passenger, deleted: true };
					}
					return e;
				});
		};
		store.update(
			updateEntities(reservation_id, {
				passengers: updater(passengers),
			})
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - selectTemplatePassenger
	 **-------------------------------------------------------------------------------------
	 */
	public selectTemplatePassenger() {
		return {
			uuid: uuid.v4(),
			reservation_additional_operator_id: -1,
			name: null,
			email: null,
			dob: null,
			type: null,
			isDriving: null,
			isMinor: null,
			hasError: false,
			signature: null,
			deleted: null,
			documents: {
				waiver: null,
				rentalAgreement: null,
				navRules: null,
				acknowledgement: null,
			},
			documentsSigned: null,
		};
	}
}
